import {
  CheckCircle2,
  Package,
  Truck,
  MapPin,
  Calendar,
  CreditCard,
  DollarSign,
  BarChart,
  BarChart3,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTracking } from '../hooks/useTracking';
import { Loader } from './ui/Loader';
import { EmptyScreen } from './ui/EmptyScreen';
import { formatDate } from '../libs/utils';
import { getShipmentStatusClasses, formatStatusDisplay } from '../utils/status';

export default function TrackingDetails({
  trackingId = 'Delhivery - 4060251007813',
  rtoTrackingId,
  embedded = false,
  orderStatus,
}: any) {
  // If only rtoTrackingId is provided (no forward AWB), open in return-only mode
  const isReturnOnly = !trackingId && Boolean(rtoTrackingId);
  const effectiveTrackingId = isReturnOnly ? rtoTrackingId : trackingId;

  const { data, loading, error } = useTracking(effectiveTrackingId);

  // Detect if any event in the history is an RTO-type status
  const isRtoStatus = (status: string) => {
    const s = status.toLowerCase();
    return (
      s.includes('rto') ||
      s.includes('return to origin') ||
      (s.includes('return') && s.includes('transit'))
    );
  };

  // Also detect RTO from the passed order status (known before data loads)
  const isOrderStatusRto = orderStatus ? isRtoStatus(orderStatus) : false;

  // API returns oldest-first — work in that native order for splitting
  const apiHistory = data?.tracking_history ?? [];

  // Find the first RTO event index in oldest-first order
  const rtoStartIndex = apiHistory.findIndex((item: any) =>
    isRtoStatus(item.status)
  );
  const hasRtoInHistory = rtoStartIndex !== -1;

  // hasRTO = separate rtoTrackingId supplied OR history contains RTO events
  const hasRTO = Boolean(rtoTrackingId) || hasRtoInHistory;

  // Start on Return tab if order status already tells us it's RTO, or return-only mode
  const [activeTab, setActiveTab] = useState<'forward' | 'return'>(
    isReturnOnly || isOrderStatusRto ? 'return' : 'forward'
  );

  // After data loads, auto-switch to Return tab if RTO events are detected in history
  useEffect(() => {
    if (!loading && hasRtoInHistory && !isReturnOnly) {
      setActiveTab('return');
    }
  }, [loading, hasRtoInHistory, isReturnOnly]);

  // Split in oldest-first order, then reverse each half so the UI shows newest-first
  const forwardApiSlice =
    hasRtoInHistory && !isReturnOnly
      ? apiHistory.slice(0, rtoStartIndex) // everything before first RTO
      : isReturnOnly
        ? []
        : apiHistory;

  const returnApiSlice =
    hasRtoInHistory && !isReturnOnly
      ? apiHistory.slice(rtoStartIndex) // from first RTO onward
      : isReturnOnly
        ? apiHistory
        : [];

  const mapHistory = (items: any[]) =>
    [...items].reverse().map((item: any) => {
      const detail = item.message_detail || item.messageDetail || item.remark || item.activity || '';
      const message = item.status_message || item.statusMessage || (detail && detail.trim().toLowerCase() !== (item.status || '').trim().toLowerCase() ? detail : '');
      return {
        date: item.status_date_time || item.statusDateTime || '',
        status: item.status || 'Updated',
        location: item.location || '',
        message: message || '',
        detail: detail || '',
      };
    });

  const timeline = mapHistory(forwardApiSlice);
  const returnTimeline = mapHistory(returnApiSlice);
  const activeTimeline = activeTab === 'forward' ? timeline : returnTimeline;

  const activeTrackingId =
    activeTab === 'return' && rtoTrackingId ? rtoTrackingId : trackingId;

  const orderData = {
    orderId: data?.order_number,
    orderDate: data?.status_date_time,
    paymentMode: data?.payment_mode,
    totalAmount: `₹${data?.total_invoice_value}`,
    items: [data?.package_detail?.package_content],
  };

  const getActiveStep = (status?: string): number => {
    if (!status) return 0;
    const s = status.toLowerCase();
    if (s.includes('delivered') && !isRtoStatus(status)) return 3;
    if (s.includes('transit') || s.includes('delivery')) return 2;
    if (s.includes('picked up') || s.includes('pickup scheduled')) return 1;
    return 0;
  };

  // Progress bar reflects the last forward event (not the RTO status)
  const lastForwardStatus =
    forwardApiSlice.length > 0
      ? forwardApiSlice[forwardApiSlice.length - 1].status
      : data?.status;

  const activeStep = getActiveStep(
    activeTab === 'forward' ? lastForwardStatus : undefined
  );

  const steps = [
    { label: 'Order Placed', completed: activeStep >= 0 },
    { label: 'Picked Up', completed: activeStep >= 1 },
    { label: 'In Transit', completed: activeStep >= 2 },
    { label: 'Delivered', completed: activeStep >= 3 },
  ];



  if (loading) {
    return (
      <div className="min-h-[300px] flex items-center justify-center">
        <Loader text="Loading Tracking Details..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[300px] flex items-center justify-center">
        <EmptyScreen
          title="Tracking Error"
          description={
            error ||
            "We couldn't retrieve the tracking information for this shipment."
          }
        />
      </div>
    );
  }
  // Embedded/simple version
  if (embedded) {
    return (
      <div className="bg-white p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
          <div className="min-w-0">
            <p className="text-sm text-gray-500 break-all">
              Tracking ID:{' '}
              <span className="font-medium text-gray-900">{trackingId}</span>
            </p>
          </div>
          <div className="px-3 py-1 bg-green-50 text-green-700 border border-green-200 rounded-full text-xs sm:text-sm font-medium shrink-0">
            {formatStatusDisplay(data?.status || orderStatus || 'N/A')}
          </div>
        </div>

        {/* Progress steps */}
        <div className="relative mb-8">
          <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200"></div>
          <div
            className="absolute top-4 left-0 h-0.5 bg-teal transition-all duration-500"
            style={{ width: `${(activeStep / (steps.length - 1)) * 100}%` }}
          ></div>
          <div className="relative flex justify-between">
            {steps.map((step, index) => (
              <div key={index} className="flex flex-col items-center">
                <div
                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center border-2 border-white 
                  ${step.completed ? 'bg-teal text-white' : 'bg-gray-200 text-gray-500'} z-10`}
                >
                  <CheckCircle2 size={16} />
                </div>
                <span className="text-[10px] sm:text-xs font-medium text-gray-700 mt-2 text-center leading-tight">
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <h3 className="text-sm font-semibold text-gray-900">Latest Updates</h3>
        <div className="max-h-[320px] mt-2 overflow-y-auto pr-2 space-y-4 scrollbar-thin scrollbar-thumb-gray-200">
          {activeTimeline.length > 0 ? (
            activeTimeline.map((entry, index) => {
              const displayMsg = entry.message || entry.detail;
              const showMsg = displayMsg && displayMsg.trim().toLowerCase() !== entry.status.trim().toLowerCase();

              return (
                <div key={index} className="flex items-start gap-3">
                  <div
                    className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 
                  ${
                    entry.status === 'Delivered'
                      ? 'bg-green-500'
                      : entry.status === 'Out For Delivery'
                        ? 'bg-blue-500'
                        : entry.status === 'Exception'
                          ? 'bg-orange-500'
                          : entry.status === 'RTO Initiated'
                            ? 'bg-red-500'
                            : entry.status === 'In Transit'
                              ? 'bg-purple-500'
                              : 'bg-gray-400'
                  }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center justify-between gap-1">
                      <span className="text-sm font-semibold text-gray-900 break-words">
                        {entry.status}
                      </span>
                      <span className="text-[11px] sm:text-xs text-gray-500 font-mono">
                        {entry.date}
                      </span>
                    </div>

                    {showMsg && (
                      <p className="text-xs text-gray-800 font-medium mt-1 break-words">
                        {displayMsg}
                      </p>
                    )}

                    {entry.location && (
                      <p className="text-xs text-gray-500 mt-1 flex items-center gap-1 break-words">
                        <MapPin size={12} className="flex-shrink-0 text-gray-400" />
                        <span>{entry.location}</span>
                      </p>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-xs text-gray-500 italic">
              No tracking updates available yet.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Full detailed version
  return (
    <div className="min-h-screen ">
      <div className="max-w-4xl mx-auto">
        {/* Header Card */}
        <div className="bg-sidebar rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Truck className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">
                    Tracking Details
                  </h1>
                  <p className="text-gray-100 mt-1">
                    Real-time updates for your shipment
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-start lg:items-end gap-2">
              <div className="px-3 py-1 bg-green-50 text-xs 2xl:text-sm text-green-700 border border-green-200 rounded-lg font-semibold">
                {formatStatusDisplay(data?.status || 'N/A')}
              </div>
              <p className="text-sm text-gray-100">
                {data?.estimated_delivery_date
                  ? `EDD: ${data.estimated_delivery_date}`
                  : ''}
              </p>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t">
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
              <div>
                {/* asdsad */}
                <p className="text-base text-gray-100">Tracking ID: </p>
                <p className="text-lg font-semibold text-gray-200">
                  {activeTrackingId}
                </p>
              </div>

              <div className="flex items-center gap-4">
                <div>
                  <p className="text-sm text-gray-100">Order ID</p>
                  <p className="font-medium text-gray-200">
                    {orderData.orderId}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-200">Order Date</p>
                  <p className="font-medium text-gray-100">
                    {orderData.orderDate}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {hasRTO && (
          <div className="flex gap-2 mb-6">
            {!isReturnOnly && (
              <button
                onClick={() => setActiveTab('forward')}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  activeTab === 'forward'
                    ? 'bg-teal text-white border-teal'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-teal hover:text-teal'
                }`}
              >
                Forward
              </button>
            )}
            <button
              onClick={() => setActiveTab('return')}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                activeTab === 'return'
                  ? 'bg-teal text-white border-teal'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-teal hover:text-teal'
              }`}
            >
              Return
            </button>
          </div>
        )}
        {/* Timeline */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            {activeTab === 'return' ? 'RTO Timeline' : 'Timeline'}
          </h2>
          <div className="space-y-6">
            {activeTimeline.map((entry, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      entry.status === 'Delivered'
                        ? 'bg-green-500'
                        : entry.status === 'Out For Delivery'
                          ? 'bg-blue-500'
                          : entry.status === 'Exception'
                            ? 'bg-orange-500'
                            : entry.status === 'RTO Initiated'
                              ? 'bg-red-500'
                              : entry.status === 'In Transit'
                                ? 'bg-purple-500'
                                : 'bg-gray-400'
                    }`}
                  />
                  {index < activeTimeline.length - 1 && (
                    <div className="w-0.5 h-8 bg-gray-200 mt-1"></div>
                  )}
                </div>

                <div className="flex-1">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getShipmentStatusClasses(entry.status)} ${entry.status === 'Out For Delivery' ? 'animate-pulse' : ''}`}
                    >
                      {formatStatusDisplay(entry.status)}
                    </span>
                    <span className="text-sm text-gray-600">{entry.date}</span>
                  </div>
                  {entry.message && entry.message.trim().toLowerCase() !== entry.status.trim().toLowerCase() && (
                    <p className="text-sm font-medium text-gray-800 mt-1 break-words">
                      {entry.message}
                    </p>
                  )}
                  {entry.detail && entry.detail.trim().toLowerCase() !== (entry.message || '').trim().toLowerCase() && entry.detail.trim().toLowerCase() !== entry.status.trim().toLowerCase() && (
                    <p className="text-xs text-gray-500 mt-1 italic break-words">
                      {formatStatusDisplay(entry.detail)}
                    </p>
                  )}
                  <div className="flex items-start gap-2 mt-2">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-600">{entry.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Order & Package Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Order Details */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Order Information
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Order ID</p>
                <p className="font-medium text-gray-900">{orderData.orderId}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Order Date</p>
                <p className="font-medium text-gray-900">
                  {orderData.orderDate}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Payment Method</p>
                <p className="font-medium text-gray-900">
                  {orderData.paymentMode}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Amount</p>
                <p className="text-lg font-bold text-gray-900">
                  {orderData.totalAmount}
                </p>
              </div>
            </div>
          </div>

          {/* Items Ordered */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Items Ordered
            </h3>
            <div className="space-y-3">
              {orderData.items.length > 0 ? (
                orderData.items.map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full bg-teal  mt-2 flex-shrink-0" />
                    <span className="text-base text-gray-700">{item}</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-text3">No items found</p>
              )}
            </div>

            <div className="mt-6 pt-6 border-t">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Package className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Items</p>
                    <p className="font-medium text-gray-900">
                      {orderData.items.length} Item
                      {orderData.items.length > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-green-50 rounded-lg">
                    <Truck className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Status</p>
                    <p className="font-medium text-gray-900">
                      {data?.status || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Note */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="p-1 bg-blue-100 rounded">
              <Calendar className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-blue-900">
                Delivery Information
              </p>
              <p className="text-sm text-blue-700 mt-1">
                {data?.status_date_time
                  ? `Latest update: ${formatDate(data.status_date_time)}`
                  : 'Tracking information available.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
